import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { ProjectContact } from './models';
import { baseApi } from '@centerpiece/components';

export const project_contact = baseApi
  .enhanceEndpoints({ addTagTypes: ['ProjectContacts', 'ProjectContact'] })
  .injectEndpoints({
    endpoints: builder => ({
      getProjectContacts: builder.query<ProjectContact[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'project_contact',
          method: 'GET',
          params: params,
        }),
        transformResponse: (data: ProjectContact[], meta, arg) => {
          const projectContacts: Array<ProjectContact> = data.map(
            (pc: ProjectContact) => {
              //flatten project quote infi
              return {
                ...pc,
                // organization:
                //   pc.contact_r?.form?.organization ||
                //   pc.contact_r?.form?.custom_fields?.find(
                //     (cf: any) => cf.organization !== ''
                //   )?.organization,
                avatar: { ...pc.contact_r, relationship: pc?.id },
              };
            }
          );
          return projectContacts;
        },
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'ProjectContact' as const,
                  id,
                })),
                'ProjectContacts',
              ]
            : ['ProjectContacts'],
      }),
      getProjectContact: builder.query<ProjectContact, string>({
        query: id => ({
          url: `project_contact/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [
          { type: 'ProjectContact', id: arg },
        ],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createProjectContact: builder.mutation({
        query: request => ({
          url: 'project_contact',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['ProjectContacts'],
      }),
      updateProjectContact: builder.mutation<ProjectContact, ProjectContact>({
        query: request => ({
          url: `project_contact/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'ProjectContact', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            project_contact.util.updateQueryData(
              'getProjectContact',
              `${id}`,
              draft => {
                Object.assign(draft, patch);
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(project_contact.util.invalidateTags(['ProjectContacts']))
             */
            dispatch(project_contact.util.invalidateTags(['ProjectContacts']));
          }
        },
      }),
      deleteProjectContact: builder.mutation<ProjectContact, number>({
        query: id => ({
          url: `project_contact/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'ProjectContact' as const, arg }, 'ProjectContacts']
            : ['ProjectContacts'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetProjectContactsQuery,
  useGetProjectContactQuery,
  useCreateProjectContactMutation,
  useUpdateProjectContactMutation,
  useDeleteProjectContactMutation,
  useLazyGetProjectContactsQuery,
  usePrefetch: useProjectContactsPrefetch,
} = project_contact;
const selectProjectContactsRoot =
  project_contact.endpoints.getProjectContacts.select({});
export const selectProjectContacts = createSelector(
  selectProjectContactsRoot,
  project_contact => project_contact.data || []
);

export const subscribeProjectContacts =
  project_contact.endpoints.getProjectContacts.useQueryState;
